import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { streamPalettes, defaultStreamPalette } from '../design/palettes';
import { LoadingScreen } from '../design/primitives';

const c = streamPalettes[defaultStreamPalette];

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen background={c.bg} color={c.accent} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Renders its children when the signed-in user has verified their email;
// otherwise diverts to the "check your inbox" gate page. Pair with
// ProtectedRoute (this assumes there's a user in context already).
export function RequireVerifiedEmail({ children }: ProtectedRouteProps) {
  const { user } = useAuth();
  if (user !== null && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }
  return <>{children}</>;
}

// Renders its children only when the signed-in user has role='admin'. Anyone
// else gets bounced to /app — the admin UI is invisible to non-admins so we
// don't leak the route's existence.
export function RequireAdmin({ children }: ProtectedRouteProps) {
  const { user } = useAuth();
  if (user === null || user.role !== 'admin') {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

// Renders its children only when the given per-user feature flag is enabled;
// otherwise bounces to /app. Keeps a flag-gated route (e.g. the Gooey+ billing
// page) invisible to users without the flag — they can't even land on it.
export function RequireFeatureFlag({
  flag,
  children,
}: ProtectedRouteProps & { flag: string }) {
  const { loading, isEnabled } = useFeatureFlags();
  // Flags are fetched after auth on every hard page load. Deciding before the
  // fetch settles would bounce a direct deep-link (or reload) on a flag-gated
  // page to /app even when the user has the flag — wait it out instead.
  if (loading) {
    return <LoadingScreen background={c.bg} color={c.accent} />;
  }
  if (!isEnabled(flag)) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}
